import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Clock, MessageSquare, Shield, Users } from "lucide-react";

import { getProjectAnalytics, MetricSeries } from "./api";
import {
  StatCard,
  StatCardError,
  StatCardSkeleton,
  StatDelta,
} from "./StatCard";
import styles from "./AnalyticsCards.module.css";

const NUM = new Intl.NumberFormat("en-US");

interface AnalyticsCardsProps {
  projectId: string;
}

// countDelta builds the delta line for Players / Guilds: a green "+N new"
// when there is data, a muted "No <noun> yet" when the project is empty.
function countDelta(m: MetricSeries, emptyText: string): StatDelta {
  if ((m.total ?? 0) === 0) {
    return { tone: "none", context: emptyText };
  }
  return {
    tone: "up",
    value: `+${NUM.format(m.last_7d)}`,
    context: "new · last 7 days",
  };
}

/**
 * AnalyticsCards renders the game-level analytics row on Project Overview:
 * Players / Guilds / Chat over a fixed 7-day window. Loads independently of
 * the rest of the page; an error degrades to em-dashes without breaking it.
 * Visual reference: docs/mockups/project-overview.html + analytics-states.html.
 */
export function AnalyticsCards({ projectId }: AnalyticsCardsProps) {
  const q = useQuery({
    queryKey: ["project", projectId, "analytics"],
    queryFn: () => getProjectAnalytics(projectId),
    enabled: !!projectId,
  });

  let body: ReactNode;
  if (q.isLoading) {
    body = (
      <>
        <StatCardSkeleton variant={0} />
        <StatCardSkeleton variant={1} />
        <StatCardSkeleton variant={2} />
      </>
    );
  } else if (q.isError || !q.data) {
    body = (
      <>
        <StatCardError label="Players" icon={<Users size={14} />} />
        <StatCardError label="Guilds" icon={<Shield size={14} />} />
        <StatCardError label="Chat messages" icon={<MessageSquare size={14} />} />
      </>
    );
  } else {
    const a = q.data;
    const chatDelta: StatDelta =
      a.chat.last_7d === 0
        ? { tone: "none", context: "No messages · last 7 days" }
        : { tone: "flat", context: "messages · last 7 days" };
    body = (
      <>
        <StatCard
          label="Players"
          icon={<Users size={14} />}
          value={NUM.format(a.players.total ?? 0)}
          delta={countDelta(a.players, "No players yet")}
          series={a.players.series}
        />
        <StatCard
          label="Guilds"
          icon={<Shield size={14} />}
          value={NUM.format(a.guilds.total ?? 0)}
          delta={countDelta(a.guilds, "No guilds yet")}
          series={a.guilds.series}
        />
        <StatCard
          label="Chat messages"
          icon={<MessageSquare size={14} />}
          value={NUM.format(a.chat.last_7d)}
          delta={chatDelta}
          series={a.chat.series}
        />
      </>
    );
  }

  // Caption matches the mockup's three states (docs/mockups: project-overview.html
  // populated row; analytics-states.html loading & empty rows).
  const allEmpty =
    !!q.data &&
    (q.data.players.total ?? 0) === 0 &&
    (q.data.guilds.total ?? 0) === 0 &&
    q.data.chat.last_7d === 0;
  let caption = "Game-level analytics · last 7 days";
  if (q.data) {
    caption = allEmpty
      ? "Game-level analytics · last 7 days · data appears once players connect via the SDK"
      : "Game-level analytics · last 7 days · computed on page open, cached ~10 min";
  }

  return (
    <div>
      <div className={styles.row}>{body}</div>
      {!q.isLoading && (q.isError || !q.data) ? (
        <div className={styles.errorStrip} role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>Couldn&apos;t load analytics. The rest of this page still works.</span>
          <button
            type="button"
            className={styles.retry}
            onClick={() => q.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.caption}>
          <Clock size={12} aria-hidden="true" />
          {caption}
        </div>
      )}
    </div>
  );
}
